import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, setAuthToken } from "@/lib/api";
import { clearSessionToken, readSessionToken, writeSessionToken } from "@/lib/authSessionStorage";


const AuthContext = createContext(null);


export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => readSessionToken());
  const [loading, setLoading] = useState(true);

  const persistSession = useCallback((nextToken, nextUser) => {
    writeSessionToken(nextToken);
    setAuthToken(nextToken);
    setToken(nextToken);
    setUser(nextUser);
  }, []);

  const clearSession = useCallback(() => {
    clearSessionToken();
    setAuthToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const response = await api.get("/auth/me");
    setUser(response.data);
    return response.data;
  }, []);

  const login = useCallback(async (payload) => {
    const response = await api.post("/auth/login", payload);
    persistSession(response.data.token, response.data.user);
    return response.data.user;
  }, [persistSession]);

  const signup = useCallback(async (payload) => {
    const response = await api.post("/auth/signup", payload);
    persistSession(response.data.token, response.data.user);
    return response.data.user;
  }, [persistSession]);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setAuthToken(token);
        await refreshUser();
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [clearSession, refreshUser, token]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      signup,
      logout,
      refreshUser,
      setUser,
    }),
    [loading, login, logout, refreshUser, signup, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth moet binnen AuthProvider gebruikt worden.");
  }

  return context;
};